import {
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Pencil,
  Plus,
  Trash2,
  TriangleAlert,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Modal } from "@/components/ui/modal";
import { refusal } from "@/lib/api/client";
import { cn } from "@/lib/utils/cn";
import { IssueIcon, thematicClasses, type ThematicClasses } from "@/lib/thematic";
import { ResourceMenu, hasAnyResource } from "@/features/resources";

import { actionPlanKeys } from "../../api/keys";
import {
  createAction,
  createGoal,
  deleteAction,
  deleteGoal,
  dismissActionsTour,
  patchAction,
  patchGoal,
  useSetActionSelection,
  useStakeholderGroups,
  useUpdateActions,
} from "../../api/hooks";
import { useStepTour, type TourStep } from "../../lib/tour";
import type {
  ActionPlanAction,
  ActionPlanDetail,
  ActionPlanGoal,
  ActionPlanIssue,
  StakeholderGroup,
} from "../../api/types";
import {
  actionName,
  cascadeWouldOverwrite,
  goalGroupConsensus,
  goalName,
  goalStats,
  goalsMissingSelection,
  isIssueIncluded,
} from "../../lib/selection";
import { ConsultationModal } from "../ConsultationModal";
import { StepSideNav } from "../StepSideNav";
import { Badge } from "../ui/Badge";
import { Checkbox } from "../ui/Checkbox";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { goalAnchor, jumpToGoal } from "../../lib/anchors";
import { buildNavIssues } from "../../lib/step-nav";
import { StepIntro } from "./StepIntro";

/**
 * Reword modal — a single textarea prefilled with the effective name, with the
 * original name as the placeholder. Matches the old app's customization form:
 * no "use custom name" checkbox — `use_custom_*` is derived from whether text
 * remains (clearing the field reverts to the original name).
 */
function RewordModal({
  title,
  label,
  help,
  original,
  initial,
  requireValue = false,
  onSave,
  onClose,
}: {
  title: string;
  label: string;
  /** Explanatory copy shown above the field (mirrors the old app's modal intro). */
  help?: string;
  original: string;
  initial: string;
  /** Create mode: disallow saving an empty value. */
  requireValue?: boolean;
  onSave: (text: string) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(value.trim());
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <div className="space-y-4">
        {help && <p className="text-sm text-muted-foreground">{help}</p>}
        <label className="block space-y-1.5">
          <span className="text-sm font-medium">{label}</span>
          <textarea
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder={original}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t("actionPlans.actions.cancel")}
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || (requireValue && !value.trim())}
          >
            {t("actionPlans.actions.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The objective editor: its name and every one of its courses of action in one
 * form, plus rows for new ones — mirroring the old app's customization modal
 * (goal form + actions formset + extra-actions formset), rather than making the
 * user open one modal per line. Clearing a field reverts it to the catalogue
 * wording, same rule as the single-field reword.
 */
export function ObjectiveEditor({
  goal,
  planId,
  onClose,
}: {
  goal: ActionPlanGoal;
  planId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(goalName(goal));
  const [names, setNames] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      goal.action_plan_actions.map((a) => [a.id, actionName(a)]),
    ),
  );
  const [added, setAdded] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (name.trim() !== goalName(goal)) {
        await patchGoal(goal.id, { custom_name: name.trim(), action_plan: planId });
      }
      for (const action of goal.action_plan_actions) {
        const next = (names[action.id] ?? "").trim();
        if (next !== actionName(action)) {
          await patchAction(action.id, { custom_name: next });
        }
      }
      for (const fresh of added) {
        if (fresh.trim()) await createAction(planId, goal.id, fresh.trim());
      }
      void qc.invalidateQueries({ queryKey: actionPlanKeys.detail(planId) });
      onClose();
    } catch (err) {
      toast.error(refusal(err) ?? t("actionPlans.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const field =
    "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <Modal title={t("actionPlans.actions.editObjective")} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t("actionPlans.actions.editObjectiveHelp")}
        </p>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium">
            {t("actionPlans.actions.rewordGoalLabel")}
          </span>
          <textarea
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder={goal.name}
            className={field}
          />
        </label>

        <div className="space-y-2">
          <p className="text-sm font-medium">
            {t("actionPlans.actions.editObjectiveActions")}
          </p>
          {goal.action_plan_actions.map((action) => (
            <textarea
              key={action.id}
              value={names[action.id] ?? ""}
              onChange={(e) =>
                setNames((prev) => ({ ...prev, [action.id]: e.target.value }))
              }
              rows={2}
              maxLength={500}
              placeholder={action.name}
              aria-label={action.name}
              className={field}
            />
          ))}
          {added.map((value, index) => (
            <textarea
              key={`new-${index}`}
              autoFocus
              value={value}
              onChange={(e) =>
                setAdded((prev) =>
                  prev.map((v, i) => (i === index ? e.target.value : v)),
                )
              }
              rows={2}
              maxLength={400}
              placeholder={t("actionPlans.actions.rewordActionLabel")}
              aria-label={t("actionPlans.actions.addAction")}
              className={field}
            />
          ))}
          <button
            type="button"
            onClick={() => setAdded((prev) => [...prev, ""])}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            <Plus className="size-4" aria-hidden />
            {t("actionPlans.actions.addAction")}
          </button>
        </div>

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t("actionPlans.actions.cancel")}
          </Button>
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? t("actionPlans.actions.saving") : t("actionPlans.actions.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Shared trigger styling for the two stakeholder-group pickers. */
const GROUP_TRIGGER =
  "flex cursor-pointer list-none items-center gap-1.5 rounded-md border border-input px-2.5 py-1 text-xs font-semibold hover:bg-accent";

/**
 * Close a <details> menu on an outside click or Escape. Without it every picker
 * ever opened stays open, and since the panels are absolutely positioned they
 * stack over the rows below — very visible now that a row and its objective
 * each have one.
 */
function useDismissable(ref: React.RefObject<HTMLDetailsElement | null>) {
  useEffect(() => {
    const close = (event: Event) => {
      const el = ref.current;
      if (el?.open && !el.contains(event.target as Node)) el.open = false;
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && ref.current?.open) ref.current.open = false;
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [ref]);
}

/**
 * The shared shell for both stakeholder pickers: the trigger, its truncation
 * and the checkbox menu. Only the label ladder and what a tick writes differ
 * between the per-action and per-objective versions, so those are the props.
 */
function GroupPicker({
  label,
  title,
  tourId,
  width,
  disabled = false,
  menuDisabled = false,
  groups,
  isChecked,
  onToggle,
}: {
  label: string;
  title?: string;
  tourId: string;
  width: string;
  /** Greys the trigger out and stops it opening (nothing to assign to). */
  disabled?: boolean;
  /** Keeps the menu open but inert while a write is in flight. */
  menuDisabled?: boolean;
  groups: StakeholderGroup[];
  isChecked: (id: string) => boolean;
  onToggle: (id: string, on: boolean) => void;
}) {
  const ref = useRef<HTMLDetailsElement>(null);
  useDismissable(ref);
  return (
    <details ref={ref} className={cn("relative shrink-0", width)} data-tour={tourId}>
      <summary
        title={title}
        className={cn(
          GROUP_TRIGGER,
          disabled ? "pointer-events-none opacity-50" : "text-muted-foreground",
        )}
      >
        <Users className="size-3.5 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <ChevronDown className="size-3 shrink-0" aria-hidden />
      </summary>
      <GroupMenu
        groups={groups}
        isChecked={isChecked}
        onToggle={onToggle}
        disabled={menuDisabled}
      />
    </details>
  );
}

function GroupMenu({
  groups,
  isChecked,
  onToggle,
  disabled,
}: {
  groups: StakeholderGroup[];
  isChecked: (id: string) => boolean;
  onToggle: (id: string, on: boolean) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="absolute right-0 z-10 mt-1 w-60 rounded-lg border bg-popover p-2 shadow-lg">
      {groups.length === 0 ? (
        <p className="px-1 py-1 text-xs text-muted-foreground">
          {t("actionPlans.actions.noGroupsYet")}
        </p>
      ) : (
        groups.map((g) => (
          <label
            key={g.id}
            className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-accent"
          >
            <input
              type="checkbox"
              className="size-4 accent-primary"
              checked={isChecked(g.id)}
              disabled={disabled}
              onChange={(e) => onToggle(g.id, e.target.checked)}
            />
            {g.name}
          </label>
        ))
      )}
    </div>
  );
}

/** Per-action multiselect of stakeholder groups to consult (collapses to a count
 *  past two, matching the design). */
function GroupAssign({
  action,
  planId,
  groups,
}: {
  action: ActionPlanAction;
  planId: string;
  groups: StakeholderGroup[];
}) {
  const { t } = useTranslation();
  const updateActions = useUpdateActions(planId);
  const assigned = new Set(action.consultation_groups ?? []);

  const toggle = (groupId: string, on: boolean) => {
    const next = new Set(assigned);
    if (on) next.add(groupId);
    else next.delete(groupId);
    updateActions.mutate([
      { id: action.id, body: { consultation_groups: [...next] } },
    ]);
  };

  const names = groups.filter((g) => assigned.has(g.id)).map((g) => g.name);
  const label =
    names.length === 0
      ? t("actionPlans.actions.selectGroups")
      : names.length <= 2
        ? names.join(", ")
        : t("actionPlans.actions.groupsCount", { count: names.length });

  return (
    // Fixed width from sm up: the summary text varies per row (placeholder /
    // group names / a count), and letting it size itself left every row's
    // control ending at a different x.
    <GroupPicker
      tourId="ap-groups"
      width="sm:w-44"
      label={label}
      title={names.join(", ") || undefined}
      groups={groups}
      isChecked={(id) => assigned.has(id)}
      onToggle={toggle}
      menuDisabled={updateActions.isPending}
    />
  );
}

/**
 * Objective-level stakeholder groups: pick once, cascade to every *retained*
 * action beneath (matching the old app). The picker shows the set the retained
 * actions already share, or "mixte" when they diverge. Overwriting hand-picked
 * per-action choices asks first — filling in empty ones doesn't.
 */
function GoalGroupAssign({
  goal,
  planId,
  groups,
}: {
  goal: ActionPlanGoal;
  planId: string;
  groups: StakeholderGroup[];
}) {
  const { t } = useTranslation();
  const updateActions = useUpdateActions(planId);
  const [pending, setPending] = useState<string[] | null>(null);
  const consensus = goalGroupConsensus(goal);
  const stats = goalStats(goal);
  const selectedActions = goal.action_plan_actions.filter((a) => a.is_selected);

  const cascade = (ids: string[]) =>
    updateActions.mutate(
      selectedActions.map((a) => ({
        id: a.id,
        body: { consultation_groups: ids },
      })),
    );

  const request = (groupId: string, on: boolean) => {
    const base = new Set(consensus ?? []);
    if (on) base.add(groupId);
    else base.delete(groupId);
    const ids = [...base];
    if (cascadeWouldOverwrite(goal, ids)) setPending(ids);
    else cascade(ids);
  };

  const names = groups
    .filter((g) => (consensus ?? []).includes(g.id))
    .map((g) => g.name);
  const label =
    stats.selected === 0
      ? t("actionPlans.actions.goalGroupsNoSelection")
      : consensus === null
        ? t("actionPlans.actions.goalGroupsMixed")
        : names.length === 0
          ? t("actionPlans.actions.goalGroupsAssign")
          : names.length <= 2
            ? names.join(", ")
            : t("actionPlans.actions.groupsCount", { count: names.length });

  return (
    <>
      <GroupPicker
        tourId="ap-goal-groups"
        width="sm:w-48"
        label={label}
        title={t("actionPlans.actions.goalGroupsTitle")}
        disabled={stats.selected === 0}
        groups={groups}
        isChecked={(id) => (consensus ?? []).includes(id)}
        onToggle={request}
        menuDisabled={updateActions.isPending}
      />
      {pending && (
        <ConfirmDialog
          title={t("actionPlans.actions.cascadeTitle")}
          message={t("actionPlans.actions.cascadeBody", {
            count: stats.selected,
          })}
          confirmLabel={t("actionPlans.actions.cascadeConfirm")}
          onConfirm={() => {
            cascade(pending);
            setPending(null);
          }}
          onClose={() => setPending(null)}
        />
      )}
    </>
  );
}

/**
 * Comment control — matches the old app: a filled icon when a comment exists
 * (outline when not); hovering previews the comment in a popover; clicking opens
 * a modal to view / edit it.
 */
function ActionComment({ action, planId }: { action: ActionPlanAction; planId: string }) {
  const { t } = useTranslation();
  const updateActions = useUpdateActions(planId);
  const [open, setOpen] = useState(false);
  const hasComment = Boolean(action.comment);

  const save = (value: string) =>
    new Promise<void>((resolve) => {
      const next = value.trim();
      if (next === (action.comment ?? "")) {
        resolve();
        return;
      }
      updateActions.mutate([{ id: action.id, body: { comment: next } }], {
        onSettled: () => resolve(),
      });
    });

  return (
    <div className="group relative" data-tour="ap-comment">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={hasComment ? t("actionPlans.actions.viewComment") : t("actionPlans.actions.addComment")}
        title={hasComment ? t("actionPlans.actions.viewComment") : t("actionPlans.actions.addComment")}
        className={cn(
          "grid size-7 place-items-center rounded-md hover:bg-accent [&_svg]:size-4",
          hasComment ? "text-primary" : "text-muted-foreground",
        )}
      >
        <MessageSquare aria-hidden fill={hasComment ? "currentColor" : "none"} />
      </button>
      {/* Hover preview (only when a comment exists) */}
      {hasComment && (
        <div className="pointer-events-none absolute right-0 top-full z-20 mt-1 hidden w-64 rounded-md border border-border bg-popover p-2.5 text-sm text-popover-foreground shadow-lg group-hover:block">
          <p className="whitespace-pre-wrap break-words">{action.comment}</p>
        </div>
      )}
      {open && (
        <CommentModal
          initial={action.comment ?? ""}
          onSave={save}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

/** View / edit an action's comment in a modal (opened from the comment icon). */
function CommentModal({
  initial,
  onSave,
  onClose,
}: {
  initial: string;
  onSave: (text: string) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(value);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await onSave("");
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={t("actionPlans.actions.comment")} onClose={onClose}>
      <div className="space-y-4">
        <textarea
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={4}
          maxLength={1000}
          placeholder={t("actionPlans.actions.commentPlaceholder")}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <div className="flex items-center justify-between gap-2">
          {/* Delete shows only when a comment exists — clears + saves in one
              click, mirroring the old app's conditional "Supprimer" button. */}
          {initial ? (
            <Button
              variant="ghost"
              onClick={() => void remove()}
              disabled={saving}
              className="text-destructive hover:text-destructive"
            >
              {t("actionPlans.actions.deleteComment")}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              {t("actionPlans.actions.cancel")}
            </Button>
            <Button onClick={() => void save()} disabled={saving}>
              {t("actionPlans.actions.save")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function ActionRow({
  action,
  plan,
  groups,
}: {
  action: ActionPlanAction;
  plan: ActionPlanDetail;
  groups: StakeholderGroup[];
}) {
  const planId = plan.id;
  const { t } = useTranslation();
  const qc = useQueryClient();
  const setSelection = useSetActionSelection(planId);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const hasResources =
    Boolean(action.original_action) && hasAnyResource(plan, action);

  const removeAction = async () => {
    setDeleting(true);
    try {
      await deleteAction(action.id);
      setConfirmingDelete(false);
      void qc.invalidateQueries({ queryKey: actionPlanKeys.detail(planId) });
    } catch (err) {
      // The service says why it refused (an objective keeps a selected piste).
      toast.error(refusal(err) ?? t("actionPlans.errors.saveFailed"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className={cn(
        "flex items-center gap-2 border-t border-border/60 py-1 pl-8 pr-3",
        action.is_selected && "bg-muted/40",
      )}
      data-tour="ap-select"
    >
      <Checkbox
        checked={Boolean(action.is_selected)}
        onCheckedChange={(v) =>
          setSelection.mutate({ actionIds: [action.id], isSelected: v })
        }
        label={actionName(action)}
        className={cn(
          "min-h-0 min-w-0 flex-1 gap-2.5 py-1 text-[13px]",
          action.is_selected ? "font-medium" : "text-muted-foreground",
        )}
      />
      {action.is_extra_action && (
        <Badge tone="info">{t("actionPlans.actions.extraBadge")}</Badge>
      )}
      {/* Order matters: the two conditional controls sit *inside* the two that
          every row has, so the comment and group controls land in the same
          column on every row without reserving empty slots for the others.
          Only custom (extra) actions can be deleted — seeded ones are
          deselected via the checkbox, matching the old app. */}
      {action.is_extra_action && (
        <button
          type="button"
          onClick={() => setConfirmingDelete(true)}
          disabled={deleting}
          aria-label={t("actionPlans.actions.deleteAction")}
          title={t("actionPlans.actions.deleteAction")}
          className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-destructive [&_svg]:size-4"
        >
          <Trash2 aria-hidden />
        </button>
      )}
      {/* Favourites-first from here on: the enterprise has already browsed
          during the autodiagnostic. */}
      {hasResources && (
        <ResourceMenu
          actionId={action.original_action}
          actionName={actionName(action)}
          directories={plan}
          availability={action}
          variant="short"
          favoritesDefault
        />
      )}
      <ActionComment action={action} planId={planId} />
      <GroupAssign action={action} planId={planId} groups={groups} />
      {confirmingDelete && (
        <ConfirmDialog
          title={t("actionPlans.actions.deleteAction")}
          message={t("actionPlans.actions.confirmDeleteAction")}
          confirmLabel={t("actionPlans.actions.deleteAction")}
          busy={deleting}
          onConfirm={() => void removeAction()}
          onClose={() => setConfirmingDelete(false)}
        />
      )}
    </div>
  );
}

/** "Add a course of action" button → create-mode modal for an extra action. */
function AddActionButton({ goalId, planId }: { goalId: string; planId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const create = async (text: string) => {
    if (!text) return;
    await createAction(planId, goalId, text);
    void qc.invalidateQueries({ queryKey: actionPlanKeys.detail(planId) });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-primary hover:underline"
      >
        <Plus className="size-3.5" aria-hidden />
        {t("actionPlans.actions.addAction")}
      </button>
      {open && (
        <RewordModal
          title={t("actionPlans.actions.addAction")}
          label={t("actionPlans.actions.rewordActionLabel")}
          original=""
          initial=""
          requireValue
          onSave={create}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/**
 * One objective: its header owns everything that applies to the whole set —
 * select all, the cascading group picker, and the editor — leaving each action
 * row to carry only its own business.
 */
function ObjectiveCard({
  goal,
  plan,
  groups,
}: {
  goal: ActionPlanGoal;
  plan: ActionPlanDetail;
  groups: StakeholderGroup[];
}) {
  const planId = plan.id;
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Select-all covers every action under the objective, so it goes through the
  // bulk endpoint rather than one PATCH each.
  const setSelection = useSetActionSelection(planId);
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const stats = goalStats(goal);
  const allSelected = stats.actions > 0 && stats.selected === stats.actions;
  const toggleAll = (selected: boolean) => {
    const changed = goal.action_plan_actions.filter(
      (a) => Boolean(a.is_selected) !== selected,
    );
    if (changed.length === 0) return;
    setSelection.mutate({
      actionIds: changed.map((a) => a.id),
      isSelected: selected,
    });
  };

  const removeGoal = async () => {
    setDeleting(true);
    try {
      await deleteGoal(goal.id, planId);
      setConfirmingDelete(false);
      void qc.invalidateQueries({ queryKey: actionPlanKeys.detail(planId) });
    } catch (err) {
      // The service says why it refused (an objective keeps a selected piste).
      toast.error(refusal(err) ?? t("actionPlans.errors.saveFailed"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div id={goalAnchor(goal.id)} className="scroll-mt-4 border-t">
      <div className="flex flex-wrap items-center gap-2 bg-muted/50 px-3 py-1.5">
        <Checkbox
          checked={allSelected}
          disabled={stats.actions === 0}
          onCheckedChange={() => toggleAll(!allSelected)}
          label={goalName(goal)}
          hideLabel
          className="min-h-0 min-w-0"
        />
        <Badge tone={goal.is_extra_goal ? "info" : "neutral"}>
          {goal.is_extra_goal
            ? t("actionPlans.actions.extraGoalBadge")
            : t("actionPlans.actions.goalBadge")}
        </Badge>
        <h3 className="min-w-0 flex-1 truncate text-[13px] font-bold">
          {goalName(goal)}
        </h3>
        <span
          className={cn(
            "shrink-0 text-[11px] font-bold tabular-nums",
            stats.selected === 0 ? "text-warning" : "text-muted-foreground",
          )}
        >
          {stats.selected}/{stats.actions}
        </span>
        {/* Same rule as the action rows: the conditional delete sits inside the
            controls every objective has, and the group picker takes the right
            edge so it lines up with the pickers on the rows below. */}
        {goal.is_extra_goal && (
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            disabled={deleting}
            aria-label={t("actionPlans.actions.deleteGoal")}
            title={t("actionPlans.actions.deleteGoal")}
            className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-destructive [&_svg]:size-3.5"
          >
            <Trash2 aria-hidden />
          </button>
        )}
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label={t("actionPlans.actions.editObjective")}
          title={t("actionPlans.actions.editObjective")}
          data-tour="ap-reword-goal"
          className="grid size-7 shrink-0 place-items-center rounded-md border border-input text-muted-foreground hover:bg-accent [&_svg]:size-3.5"
        >
          <Pencil aria-hidden />
        </button>
        <GoalGroupAssign goal={goal} planId={planId} groups={groups} />
      </div>

      {goal.action_plan_actions.map((action) => (
        <ActionRow key={action.id} action={action} plan={plan} groups={groups} />
      ))}

      <div className="border-t border-border/60 py-1.5 pl-8">
        <AddActionButton goalId={goal.id} planId={planId} />
      </div>

      {editing && (
        <ObjectiveEditor
          goal={goal}
          planId={planId}
          onClose={() => setEditing(false)}
        />
      )}
      {confirmingDelete && (
        <ConfirmDialog
          title={t("actionPlans.actions.deleteGoal")}
          message={t("actionPlans.actions.confirmDeleteGoal")}
          confirmLabel={t("actionPlans.actions.deleteGoal")}
          busy={deleting}
          onConfirm={() => void removeGoal()}
          onClose={() => setConfirmingDelete(false)}
        />
      )}
    </div>
  );
}

/**
 * Create an objective together with its first course of action. A custom
 * objective is never created alone — the plan hierarchy only renders goals that
 * have at least one action, so both fields are required (mirrors the Django
 * "Add an objective + actions" modal).
 */
function AddGoalModal({
  onSave,
  onClose,
}: {
  onSave: (name: string, actionName: string) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [actionName, setActionName] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(name.trim(), actionName.trim());
      onClose();
    } catch (err) {
      toast.error(refusal(err) ?? t("actionPlans.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={t("actionPlans.actions.addGoal")} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t("actionPlans.actions.addGoalHelp")}
        </p>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium">
            {t("actionPlans.actions.rewordGoalLabel")}
          </span>
          <textarea
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            rows={2}
            maxLength={200}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium">
            {t("actionPlans.actions.addGoalActionLabel")}
          </span>
          <textarea
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            rows={2}
            maxLength={400}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
        {name.trim() && !actionName.trim() && (
          <p className="text-xs text-muted-foreground">
            {t("actionPlans.actions.goalNeedsAction")}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t("actionPlans.actions.cancel")}
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || !name.trim() || !actionName.trim()}
          >
            {saving ? t("actionPlans.actions.saving") : t("actionPlans.actions.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** "Add an objective" button → create-mode modal for an extra goal + its first action. */
function AddGoalButton({ issueId, planId }: { issueId: string; planId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const create = async (name: string, actionName: string) => {
    if (!name || !actionName) return;
    await createGoal(planId, issueId, name, actionName);
    void qc.invalidateQueries({ queryKey: actionPlanKeys.detail(planId) });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-tour="ap-add-goal"
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary hover:underline"
      >
        <Plus className="size-3.5" aria-hidden />
        {t("actionPlans.actions.addGoal")}
      </button>
      {open && <AddGoalModal onSave={create} onClose={() => setOpen(false)} />}
    </>
  );
}

/** One issue: a collapsible, colour-coded block of objective cards. */
function IssueBlock({
  issue,
  tc,
  plan,
  groups,
  open,
  onToggle,
}: {
  issue: ActionPlanIssue;
  tc: ThematicClasses;
  plan: ActionPlanDetail;
  groups: StakeholderGroup[];
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const actions = issue.action_plan_goals.flatMap((g) => g.action_plan_actions);
  const selected = actions.filter((a) => a.is_selected).length;

  return (
    <section className="overflow-hidden rounded-lg border bg-card" data-tour="ap-issues">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] font-bold",
          tc.softBg,
          tc.ink,
        )}
      >
        {open ? (
          <ChevronDown className="size-4 shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="size-4 shrink-0" aria-hidden />
        )}
        <IssueIcon icon={issue.icon} className="size-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{issue.name}</span>
        <span className="shrink-0 text-[11px] tabular-nums opacity-80">
          {t("actionPlans.actions.retainedOf", {
            selected,
            total: actions.length,
          })}
        </span>
      </button>
      {open && (
        <>
          {issue.action_plan_goals.map((goal) => (
            <ObjectiveCard
              key={goal.id}
              goal={goal}
              plan={plan}
              groups={groups}
            />
          ))}
          <div className="border-t px-3 py-2">
            <AddGoalButton issueId={issue.id} planId={plan.id} />
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Step 2 — courses of action & stakeholder consultation. The objective is the
 * unit of work: its header carries select-all, the group cascade and the
 * editor, and each row keeps only what belongs to that action. A side nav jumps
 * between objectives, so the step is navigable rather than merely long.
 */
export function ActionsStep({ plan }: { plan: ActionPlanDetail }) {
  const { t } = useTranslation();
  const groupsQuery = useStakeholderGroups();
  const groups = groupsQuery.data ?? [];
  const [consultOpen, setConsultOpen] = useState(false);
  /**
   * Step 1 decides which issues the plan carries; this step refines what's
   * inside them, so it shows those and nothing else — same rule as the Django
   * page, which restricts the tree to issues with any action selected.
   *
   * Snapshotted on entry: unticking an issue's last action must not make the
   * block you're working in disappear. It drops out next time you open the
   * step, exactly as the Django page drops it on the next page load.
   */
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [pendingJump, setPendingJump] = useState<string | null>(null);
  const [scopedIssueIds] = useState(
    () =>
      new Set(
        plan.action_plan_thematics
          .flatMap((th) => th.action_plan_issues)
          .filter(isIssueIncluded)
          .map((issue) => issue.id),
      ),
  );
  // Round-robin through the objectives still missing a selection, so repeated
  // clicks walk the list instead of pinning the first one.
  const missingCursor = useRef(0);

  const thematics = plan.action_plan_thematics
    .map((th) => ({
      ...th,
      action_plan_issues: th.action_plan_issues.filter(
        (issue) => scopedIssueIds.has(issue.id) || isIssueIncluded(issue),
      ),
    }))
    .filter((th) => th.action_plan_issues.length > 0);
  const allActions = thematics
    .flatMap((th) => th.action_plan_issues)
    .flatMap((i) => i.action_plan_goals)
    .flatMap((g) => g.action_plan_actions);
  const selectedCount = allActions.filter((a) => a.is_selected).length;
  const assignedCount = allActions.filter(
    (a) => a.is_selected && (a.consultation_groups ?? []).length > 0,
  ).length;
  // Scoped to what's on screen: an objective inside an issue the plan doesn't
  // carry isn't something to act on here.
  const missing = goalsMissingSelection(thematics);

  // Which issue each objective belongs to — a jump has to open its block first.
  const issueOfGoal = new Map<string, string>();
  for (const thematic of thematics) {
    for (const issue of thematic.action_plan_issues) {
      for (const goal of issue.action_plan_goals) {
        issueOfGoal.set(goal.id, issue.id);
      }
    }
  }

  // Nav: retained/total, flagging the objectives that hold nothing yet.
  const navIssues = buildNavIssues(thematics, {
    actionsOf: (goal) => goal.action_plan_actions,
    tally: (actions) => {
      const selected = actions.filter((a) => a.is_selected).length;
      return { count: `${selected}/${actions.length}`, warn: selected === 0 };
    },
  });

  const jumpTo = (goalId: string) => {
    const issueId = issueOfGoal.get(goalId);
    if (issueId) {
      setCollapsed((prev) => {
        if (!prev.has(issueId)) return prev;
        const next = new Set(prev);
        next.delete(issueId);
        return next;
      });
    }
    setPendingJump(goalId);
  };

  // Runs after the expanded block has rendered, so the anchor exists.
  useEffect(() => {
    if (!pendingJump) return;
    jumpToGoal(pendingJump);
    setPendingJump(null);
  }, [pendingJump]);

  const jumpToNextMissing = () => {
    if (missing.length === 0) return;
    const next = missing[missingCursor.current % missing.length];
    missingCursor.current += 1;
    if (next) jumpTo(next.goal.id);
  };

  const buildSteps = (): TourStep[] => [
    { selector: '[data-tour="ap-progress"]', title: t("actionPlans.tour.actions.progressTitle"), description: t("actionPlans.tour.actions.progressDesc") },
    { selector: '[data-tour="ap-consult"]', title: t("actionPlans.tour.actions.consultTitle"), description: t("actionPlans.tour.actions.consultDesc") },
    { selector: '[data-tour="ap-issues"]', title: t("actionPlans.tour.actions.issuesTitle"), description: t("actionPlans.tour.actions.issuesDesc"), side: "top" },
    { selector: '[data-tour="ap-select"]', title: t("actionPlans.tour.actions.selectTitle"), description: t("actionPlans.tour.actions.selectDesc"), side: "right" },
    { selector: '[data-tour="ap-comment"]', title: t("actionPlans.tour.actions.commentTitle"), description: t("actionPlans.tour.actions.commentDesc") },
    { selector: '[data-tour="ap-groups"]', title: t("actionPlans.tour.actions.groupsTitle"), description: t("actionPlans.tour.actions.groupsDesc") },
    { selector: '[data-tour="ap-goal-groups"]', title: t("actionPlans.tour.actions.goalGroupsTitle"), description: t("actionPlans.tour.actions.goalGroupsDesc"), side: "left" },
    { selector: '[data-tour="ap-add-goal"]', title: t("actionPlans.tour.actions.addGoalTitle"), description: t("actionPlans.tour.actions.addGoalDesc"), side: "left" },
    { selector: '[data-tour="ap-reword-goal"]', title: t("actionPlans.tour.actions.rewordGoalTitle"), description: t("actionPlans.tour.actions.rewordGoalDesc"), side: "left" },
  ];
  const qc = useQueryClient();
  const { replay } = useStepTour({
    buildSteps,
    autoRun: plan.show_actions_selection_tour ?? false,
    autoRunKey: `actions:${plan.id}`,
    // Refresh the plan once the server has recorded it: the flag lives on the
    // enterprise profile, so without this the cached plan keeps saying "show me".
    onAutoDismiss: () => {
      void dismissActionsTour(plan.id).finally(() =>
        qc.invalidateQueries({ queryKey: actionPlanKeys.detail(plan.id) }),
      );
    },
  });

  // Nothing chosen in step 1 leaves this step with nothing to refine.
  if (thematics.length === 0) {
    return (
      <div className="space-y-6">
        <StepIntro
          heading={t("actionPlans.actions.heading")}
          help={t("actionPlans.actions.help")}
        />
        <Card>
          <CardContent className="space-y-1 py-6">
            <p className="font-medium">{t("actionPlans.actions.noIssues")}</p>
            <p className="text-sm text-muted-foreground">
              {t("actionPlans.actions.noIssuesHint")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <StepIntro
            heading={t("actionPlans.actions.heading")}
            help={t("actionPlans.actions.help")}
          />
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={replay}
            className="text-sm font-semibold text-primary hover:underline"
          >
            {t("actionPlans.tour.replay")}
          </button>
          <Button
            type="button"
            data-tour="ap-consult"
            onClick={() => setConsultOpen(true)}
            className="shrink-0"
          >
            <Users aria-hidden />
            {t("actionPlans.consultations.consultButton")}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/40 px-3 py-2">
        {missing.length > 0 && (
          <button
            type="button"
            onClick={jumpToNextMissing}
            className="inline-flex items-center gap-1.5 rounded-full bg-warning/15 px-2.5 py-1 text-xs font-bold text-warning hover:bg-warning/25"
          >
            <TriangleAlert className="size-3.5 shrink-0" aria-hidden />
            {t("actionPlans.actions.goalsMissing", { count: missing.length })}
          </button>
        )}
        <p className="ml-auto text-xs text-muted-foreground">
          {t("actionPlans.actions.retainedSummary", {
            selected: selectedCount,
            total: allActions.length,
            assigned: assignedCount,
          })}
        </p>
      </div>

      {/* Explicit minmax(0,…) tracks, at every breakpoint: an `auto` track is
          sized from its content's min-content, and a truncating (nowrap) label
          reports the whole string as its min-content — which pushed the single
          mobile column out to 500px instead of letting the labels truncate. */}
      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start">
        <StepSideNav issues={navIssues} onJump={jumpTo} />
        <div className="space-y-4">
          {thematics.flatMap((thematic) => {
            const tc = thematicClasses(thematic.css_class);
            return thematic.action_plan_issues.map((issue) => (
              <IssueBlock
                key={issue.id}
                issue={issue}
                tc={tc}
                plan={plan}
                groups={groups}
                open={!collapsed.has(issue.id)}
                onToggle={() =>
                  setCollapsed((prev) => {
                    const next = new Set(prev);
                    if (next.has(issue.id)) next.delete(issue.id);
                    else next.add(issue.id);
                    return next;
                  })
                }
              />
            ));
          })}
        </div>
      </div>

      {consultOpen && (
        <ConsultationModal
          planId={plan.id}
          plan={plan}
          onClose={() => setConsultOpen(false)}
        />
      )}
    </div>
  );
}
